import React, { FC, useMemo, useEffect, useState } from 'react';
import { Anchor, Badge, Box, Flex, Text, ActionIcon, Button, Loader, Menu, UnstyledButton } from '@mantine/core';
import {
    IconLinkMinus,
    IconLink,
    IconFileDescription,
    IconBookmark,
    IconFileDollar,
    IconFileInvoice,
    IconReceipt,
    IconLinkPlus,
    IconDotsVertical,
    IconExternalLink
} from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import { getEnumStringValue } from '@/utils/enums';
import Link from 'next/link';
import LinkItem from '@/PageComponents/Links/LinkItem';
import LinkPickerModal from '@/PageComponents/Links/LinkPickerModal';
import SCModal from '@/PageComponents/Modal/SCModal';
import ProjectService from '@/services/project/project-service';
import LinkedModuleList from '@/PageComponents/Links/LinkedModuleList';
import { useMutation, useQuery } from '@tanstack/react-query';
import Fetch from '@/utils/Fetch';
import invoiceService from '@/services/invoice/invoice-service';
import QuoteService from '@/services/quote/quote-service';
import purchaseOrderService from '@/services/purchase/purchase-order-service';
import { showNotification } from '@mantine/notifications';
import companyService from '@/services/company-service';
import { formatMoney, getItemAmount, getItemDocNum, getItemStatus, getModuleSingular } from '@/PageComponents/Links/linked-item-display';
import styles from '@/PageComponents/Links/LinkedItemsList.module.css';

// Request method declared outside components per guidelines
const fetchCurrencySymbol = async () => {
  try {
    return await companyService.getCurrencySymbol();
  } catch {
    return 'R';
  }
};

const LinkedItemsList: FC<{
  jobId: string;
  project?: any | null;
  projectId?: string | null;
  customerId?: string;
  onChangeProject?: (project: any | null) => void;
  // legacy specific handlers (backwards compatible)
  onOpenQuote?: (quote: any) => void;
  onOpenInvoice?: (invoice: any) => void;
  onOpenPurchaseOrder?: (purchaseOrder: any) => void;
  // new generic handlers
  onOpenItem?: (module: number, item: any) => void;
  onUnlinkItem?: (module: number, item: any) => void;
  refreshDeps?: { quotes?: any; invoices?: any; purchaseOrders?: any; queries?: any };
  // optional currency symbol provided by a higher-level stable component
  currencySymbol?: string;
  // collapsing controls
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  // how many rows show while collapsed; the rest sit behind "+N more"
  previewCount?: number;
  // notified with the number of linked items (the project counts as one)
  onTotalCountChange?: (count: number) => void;
  // bump to open the "Link item to job" options from outside; hideAddLink drops the built-in button
  linkRequest?: number;
  hideAddLink?: boolean;
}> = ({ jobId, project, projectId, customerId, onChangeProject, onOpenQuote, onOpenInvoice, onOpenPurchaseOrder, onOpenItem, onUnlinkItem, refreshDeps, currencySymbol, collapsed = true, onCollapsedChange, previewCount = 3, onTotalCountChange, linkRequest, hideAddLink = false }: any) => {
  // Project link (moved from JobDetails)
  const [showProjectLink, setShowProjectLink] = useState(false);
  const [projectLinkLockdown, setProjectLinkLockdown] = useState(true);
  const [selectedProject, setSelectedProject] = useState<any>(project ?? null);

  useEffect(() => {
    setSelectedProject(project ?? null);
  }, [project?.ID]);

  const getLinkedProject = async (id: string) => {
    const proj = await ProjectService.getProject(id);
    setSelectedProject(proj);
  };

  const linkJobToProject = (proj: any) => {
    setSelectedProject(proj);
    onChangeProject && onChangeProject(proj);
  };

  useEffect(() => {
    const setup = async () => {
      let show = false;
      const pid = projectId;
      if (pid) {
        show = true;
      } else if (customerId) {
        /*const hasProjects = await ProjectService.customerHasProjects(customerId);
        if (hasProjects) show = true;*/
          /** Only show project when it is linked: */
      }
      if (show) {
        setShowProjectLink(true);
        if (pid) {
          await getLinkedProject(pid);
        }
        setProjectLinkLockdown(false);
      } else {
        setShowProjectLink(false);
      }
    };
    setup();
  }, [projectId, customerId]);
  // Prepare invoice statuses for filtering
  const invoiceStatuses = useMemo(() => ([
    getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Draft),
    getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Unpaid),
    getEnumStringValue(Enums.InvoiceStatus, Enums.InvoiceStatus.Paid),
  ]), []);

  // Module refresh toggles keyed by module enum; flip specific module key to trigger refetch
  const [moduleRefreshQueriesToggle, setModuleRefreshQueriesToggle] = useState<Record<number, boolean>>({});
  const [moduleRefreshToggle, setModuleRefreshToggle] = useState<Record<number, boolean>>({});

  // Busy states for external unlink actions
  const [busyQuoteId, setBusyQuoteId] = useState<string | null>(null);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const [busyPOId, setBusyPOId] = useState<string | null>(null);

  // Unlink mutations for Quote, Invoice, Purchase Order
  const unlinkQuoteMutation = useMutation({
    mutationFn: async (item: any) => {
      const full = await QuoteService.getQuote(item.ID);
      const newModule = item.CustomerID ? Enums.Module.Customer : null;
      const newItemId = item.CustomerID ?? null;
      const updated = { ...full, Module: newModule, ItemID: newItemId } as any;
      return await Fetch.put({
        url: '/Quote',
        params: {
          Quote: updated,
          QuoteItems: (full as any).QuoteItems || [],
          InventorySections: (full as any).InventorySections || [],
        },
      } as any);
    },
    onMutate: (item: any) => {
      setBusyQuoteId(item?.ID ?? null);
    },
    onError: (error: any, item: any) => {
      setBusyQuoteId(null);
      showNotification({ title: 'Warning', message: error?.message || 'Failed to unlink quote', color: 'yellow.7', autoClose: 4000 });
    },
    onSuccess: (_data: any, _item: any) => {
      setBusyQuoteId(null);
      setModuleRefreshToggle((prev: any) => ({ ...prev, [Enums.Module.Quote]: !prev[Enums.Module.Quote] }));
    },
  });

  const unlinkInvoiceMutation = useMutation({
    mutationFn: async (item: any) => {
      const full = await invoiceService.getInvoice(item.ID);
      const newModule = item.CustomerID ? Enums.Module.Customer : null;
      const newItemId = item.CustomerID ?? null;
      const updated = { ...full, Module: newModule, ItemID: newItemId } as any;
      return await Fetch.put({
        url: '/Invoice',
        params: {
          Invoice: updated,
          InvoiceItems: (full as any).InvoiceItems || [],
          InventorySections: (full as any).InventorySections || [],
        },
      } as any);
    },
    onMutate: (item: any) => {
      setBusyInvoiceId(item?.ID ?? null);
    },
    onError: (error: any, _item: any) => {
      setBusyInvoiceId(null);
      showNotification({ title: 'Warning', message: error?.message || 'Failed to unlink invoice', color: 'yellow.7', autoClose: 4000 });
    },
    onSuccess: (_data: any, _item: any) => {
      setBusyInvoiceId(null);
      setModuleRefreshToggle((prev: any) => ({ ...prev, [Enums.Module.Invoice]: !prev[Enums.Module.Invoice] }));
    },
  });

  const unlinkPOMutation = useMutation({
    mutationFn: async (item: any) => {
      const full = await purchaseOrderService.getPurchaseOrder(item.ID);
      const newModule = item.SupplierID ? Enums.Module.Supplier : null;
      const newItemId = item.SupplierID ?? null;
      const updated = { ...full, Module: newModule, ItemID: newItemId } as any;
      return await Fetch.put({
        url: '/PurchaseOrder',
        params: {
          PurchaseOrder: updated,
          PurchaseOrderItems: (full as any).PurchaseOrderItems || [],
        },
      } as any);
    },
    onMutate: (item: any) => {
      setBusyPOId(item?.ID ?? null);
    },
    onError: (error: any, _item: any) => {
      setBusyPOId(null);
      showNotification({ title: 'Warning', message: error?.message || 'Failed to unlink purchase order', color: 'yellow.7', autoClose: 4000 });
    },
    onSuccess: (_data: any, _item: any) => {
      setBusyPOId(null);
      setModuleRefreshToggle((prev: any) => ({ ...prev, [Enums.Module.PurchaseOrder]: !prev[Enums.Module.PurchaseOrder] }));
    },
  });

  // Dynamic linked item modules
  const modules = useMemo(() => ([
    {
      module: Enums.Module.Query,
      heading: 'Linked Queries',
      // queries must come from Link service so that we respect link relationships
      queryMode: 'linkService' as const,
      linkType: Enums.LinkType.JobsToQueries,
      itemModuleWhenLinkedFrom: Enums.Module.JobCard,
      labelFor: (x: any) => `${x.QueryCode}${x.CustomerName ? ' - ' + x.CustomerName : ''}`,
      hrefFor: (x: any) => `/query/${x.ID}`,
      refreshDep: [refreshDeps?.queries, moduleRefreshQueriesToggle[Enums.Module.Query]],
    },
    {
      module: Enums.Module.Quote,
      heading: 'Linked Quotes',
      endpoint: '/Quote/GetQuotes',
      extraParams: { PopulatedList: false, QuoteStatusIDList: [] },
      labelFor: (x: any) => `${x.QuoteNumber}${x.CustomerName ? ' - ' + x.CustomerName : ''}`,
      refreshDep: [refreshDeps?.quotes, moduleRefreshToggle[Enums.Module.Quote]],
    },
    {
      module: Enums.Module.Invoice,
      heading: 'Linked Invoices',
      endpoint: '/Invoice/GetInvoices',
      extraParams: { InvoiceStatusIDList: invoiceStatuses },
      labelFor: (x: any) => `${x.InvoiceNumber}${x.CustomerName ? ' - ' + x.CustomerName : ''}`,
      refreshDep: [refreshDeps?.invoices, moduleRefreshToggle[Enums.Module.Invoice]],
    },
    {
      module: Enums.Module.PurchaseOrder,
      heading: 'Linked Purchase Orders',
      endpoint: '/PurchaseOrder/GetPurchaseOrders',
      extraParams: { },
      labelFor: (x: any) => `${x.PurchaseOrderNumber}${x.SupplierDescription ? ' - ' + x.SupplierDescription : ''}`,
      // opens in a drawer when the host provides a handler, otherwise links to the full page
      hrefFor: onOpenPurchaseOrder ? undefined : (x: any) => `/purchase/${x.ID}`,
      refreshDep: [refreshDeps?.purchaseOrders, moduleRefreshToggle[Enums.Module.PurchaseOrder]],
    },
  ]), [invoiceStatuses, refreshDeps?.queries, refreshDeps?.quotes, refreshDeps?.invoices, refreshDeps?.purchaseOrders, moduleRefreshQueriesToggle[Enums.Module.Query], moduleRefreshToggle[Enums.Module.Quote], moduleRefreshToggle[Enums.Module.Invoice], moduleRefreshToggle[Enums.Module.PurchaseOrder], !!onOpenPurchaseOrder]);

  // Collapsing state and calculations
  const [collapsedInternal, setCollapsedInternal] = useState<boolean>(!!collapsed);
  useEffect(() => {
    if (typeof collapsed === 'boolean') setCollapsedInternal(collapsed);
  }, [collapsed]);

  const [moduleItemCounts, setModuleItemCounts] = useState<Record<number, number>>({});
  // Track IDs of currently linked items per module so we can pass them to AddLinkOptions as exclusions
  const [moduleItemIds, setModuleItemIds] = useState<Record<number, string[]>>({});

  const totalItems = useMemo(() => Object.values(moduleItemCounts).reduce((a: any, b: any) => a + (b || 0), 0), [moduleItemCounts]);

  useEffect(() => {
    onTotalCountChange && onTotalCountChange(totalItems + (showProjectLink ? 1 : 0));
  }, [totalItems, showProjectLink]);

  // While collapsed, the first rows (in module order) show and the rest are hidden
  const moduleVisibleLimits = useMemo(() => {
    const limits: Record<number, number> = {};
    let rem = previewCount;
    for (const m of modules) {
      const count = moduleItemCounts[m.module] || 0;
      limits[m.module] = Math.min(count, rem);
      rem -= limits[m.module];
    }
    return limits;
  }, [modules, moduleItemCounts, previewCount]);
  const hiddenItems = collapsedInternal ? Math.max(0, totalItems - previewCount) : 0;

  const toggleCollapsed = () => {
    const next = !collapsedInternal;
    setCollapsedInternal(next);
    onCollapsedChange && onCollapsedChange(next);
  };

  const moduleIcon = (module?: number) => {
    switch (module) {
      case Enums.Module.Quote: return <IconFileDollar size={17} />;
      case Enums.Module.Invoice: return <IconFileInvoice size={17} />;
      case Enums.Module.PurchaseOrder: return <IconReceipt size={17} />;
      default: return <IconFileDescription size={17} />;
    }
  };

  const symbol = currencySymbol ?? 'R';

  const renderItem = (label: string, onOpen?: () => void, onUnlink?: () => void, href?: string, isBusy?: boolean, item?: any, module?: number) => {
    const singular = getModuleSingular(module as number);
    const docNum = getItemDocNum(module as number, item) || label;
    const detail = module === Enums.Module.PurchaseOrder ? (item?.SupplierDescription || item?.SupplierName) : (item?.Reference || item?.Description);
    const status = getItemStatus(module as number, item);
    const amount = getItemAmount(module as number, item);
    const open = (e: any) => { e.stopPropagation(); if (!href) { e.preventDefault(); onOpen && onOpen(); } };

    return (
      <Flex className={styles.row} align={'center'} gap={10} miw={0}>
        <Box className={styles.icon}>{moduleIcon(module)}</Box>
        <Text size={'sm'} style={{ flex: 1, minWidth: 0 }} truncate>
          {singular}{' '}
          <Anchor component={(href ? Link : 'a') as any} href={href} c={'scBlue'} fw={600} onClick={open}>{docNum}</Anchor>
          {detail ? <Text component={'span'} inherit c={'gray.7'}> · {detail}</Text> : null}
        </Text>
        {status.label && <Badge variant={'light'} color={status.color} radius={'sm'} tt={'none'} className={styles.status}>{status.label}</Badge>}
        {amount !== null && amount !== undefined && <Text size={'sm'} className={styles.amount}>{formatMoney(amount, symbol)}</Text>}
        {isBusy ? <Loader size={16} mx={8} /> :
          <Menu position={'bottom-end'} shadow={'md'} width={200} withinPortal>
            <Menu.Target>
              <ActionIcon variant={'subtle'} color={'gray.6'} aria-label={`Actions for ${singular} ${docNum}`} onClick={(e: any) => e.stopPropagation()}>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {href ?
                <Menu.Item component={Link as any} href={href} leftSection={<IconExternalLink size={16} />}>Open {singular.toLowerCase()}</Menu.Item> :
                onOpen && <Menu.Item onClick={onOpen} leftSection={<IconExternalLink size={16} />}>Open {singular.toLowerCase()}</Menu.Item>
              }
              {onUnlink && <>
                <Menu.Divider />
                <Menu.Item color={'red'} onClick={onUnlink} leftSection={<IconLinkMinus size={16} />}>Remove from job</Menu.Item>
              </>}
            </Menu.Dropdown>
          </Menu>
        }
      </Flex>
    );
  };

  return (
    <Box style={{ minWidth: 0 }}>
        <Flex gap={'xl'} style={{ minWidth: 0 }}>
            <Flex direction={'column'} flex={1} style={{ minWidth: 0 }}>
                {showProjectLink && (
                    <Box className={styles.row} py={8}>
                        <Text size={'sm'} fw={600} c={'dark.6'} mb={4}>Project</Text>
                        <LinkItem
                            setSelected={linkJobToProject}
                            selectedItem={selectedProject}
                            module={Enums.Module.Project}
                            customerID={customerId as any}
                            lockdown={projectLinkLockdown}
                            size={{ label: 'md' } as any}
                        />
                    </Box>
                )}

                <Box>
                  {modules.map((m: any) => (
                    <LinkedModuleList
                      key={m.module}
                      jobId={jobId}
                      module={m.module}
                      heading={m.heading}
                      endpoint={m.endpoint}
                      extraParams={m.extraParams}
                      labelFor={m.labelFor}
                      renderItem={renderItem}
                      refreshDep={m.refreshDep}
                      queryMode={m.queryMode}
                      linkType={m.linkType}
                      itemModuleWhenLinkedFrom={m.itemModuleWhenLinkedFrom}
                      hrefFor={m.hrefFor}
                      flat
                      // Provide busy predicate for external unlink modules so ActionIcon can show a loader
                      getItemIsBusy={m.queryMode === 'linkService' ? undefined : ((item: any) => {
                        switch (m.module) {
                          case Enums.Module.Quote:
                            return item.ID === busyQuoteId;
                          case Enums.Module.Invoice:
                            return item.ID === busyInvoiceId;
                          case Enums.Module.PurchaseOrder:
                            return item.ID === busyPOId;
                          default:
                            return false;
                        }
                      })}
                      onAfterUnlink={m.module === Enums.Module.Query ? (() => setModuleRefreshQueriesToggle((prev: any) => ({ ...prev, [m.module]: !prev[m.module] }))) : undefined}
                      onOpen={(item: any) => {
                        if (onOpenItem) return onOpenItem(m.module, item);
                        if (m.module === Enums.Module.Quote && onOpenQuote) return onOpenQuote(item);
                        if (m.module === Enums.Module.Invoice && onOpenInvoice) return onOpenInvoice(item);
                        if (m.module === Enums.Module.PurchaseOrder && onOpenPurchaseOrder) return onOpenPurchaseOrder(item);
                      }}
                      onUnlink={m.queryMode === 'linkService' ? undefined : ((item: any) => {
                        switch (m.module) {
                          case Enums.Module.Quote:
                            unlinkQuoteMutation.mutate(item);
                            break;
                          case Enums.Module.Invoice:
                            unlinkInvoiceMutation.mutate(item);
                            break;
                          case Enums.Module.PurchaseOrder:
                            unlinkPOMutation.mutate(item);
                            break;
                          default:
                            if (onUnlinkItem) onUnlinkItem(m.module, item);
                        }
                      })}
                      limit={collapsedInternal ? (moduleVisibleLimits[m.module] ?? 0) : undefined}
                      onCountChange={(count: any) => setModuleItemCounts((prev: any) => (prev[m.module] === count ? prev : { ...prev, [m.module]: count }))}
                      onIdsChange={(ids: any) => setModuleItemIds((prev: any) => {
                        const prevIds = prev[m.module] || [];
                        const same = prevIds.length === ids.length && prevIds.every((v: any, i: any) => v === ids[i]);
                        return same ? prev : { ...prev, [m.module]: ids };
                      })}
                    />
                  ))}
                  {totalItems === 0 && !showProjectLink && (
                    <Text c={'dark.6'} size={'md'} mt={'sm'}>
                      No linked items
                    </Text>
                  )}
                </Box>
                {totalItems > previewCount && (
                  <UnstyledButton className={styles.more} onClick={toggleCollapsed}>
                    {collapsedInternal ? `+${hiddenItems} more` : 'Show less'}
                  </UnstyledButton>
                )}
            </Flex>

            {/* Linking options modal launcher */}
            <Box ml={'auto'} display={hideAddLink ? 'none' : undefined}>
              <AddLinkOptions
                openRequest={linkRequest}
                jobId={jobId}
                customerId={customerId}
                projectId={projectId || selectedProject?.ID || null}
                excludedItemIdMap={moduleItemIds}
                onLinked={(module: any, mode: any) => {
                  if (mode === 'linkService' && module === Enums.Module.Query) {
                    setModuleRefreshQueriesToggle((prev: any) => ({ ...prev, [module]: !prev[module] }));
                  } else {
                    setModuleRefreshToggle((prev: any) => ({ ...prev, [module]: !prev[module] }));
                  }
                }}
                onPickProject={(proj: any) => linkJobToProject(proj)}
                currencySymbol={currencySymbol}
              />
            </Box>

        </Flex>
    </Box>
  );
};

// Local inline component to show the modal options and the modal picker
const AddLinkOptions: FC<{ openRequest?: number; jobId: string; customerId?: string; projectId?: string | null; excludedItemIdMap?: Record<number, string[]>; onLinked: (module: number, mode: 'linkService' | 'endpoint') => void; onPickProject: (proj: any) => void; currencySymbol?: string }> = ({ openRequest, jobId, customerId, projectId, excludedItemIdMap, onLinked, onPickProject, currencySymbol }: any) => {
  const [optionsOpen, setOptionsOpen] = useState(false);

  useEffect(() => {
    if (openRequest) setOptionsOpen(true);
  }, [openRequest]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [option, setOption] = useState<any | null>(null);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const effectiveCurrencySymbol = currencySymbol ?? 'R';


  const loadExcludedIds = async (mod: number) => {
    // Exclusions are always provided by parent now
    if (mod === Enums.Module.Project) {
      setExcludedIds(projectId ? [projectId] as string[] : []);
      return;
    }
    const provided = excludedItemIdMap?.[mod];
    setExcludedIds(Array.isArray(provided) ? (provided as string[]) : []);
  };

  const options = useMemo(() => ([
      { key: 'projects', label: 'Project', module: Enums.Module.Project, title: 'Link Projects', mode: 'pickOnly' as const, icon: <IconBookmark size={18} /> },
      { key: 'queries', label: 'Query', module: Enums.Module.Query, title: 'Link Queries', mode: 'linkService' as const, linkType: Enums.LinkType.JobsToQueries, icon: <IconFileDescription size={18} /> },
      { key: 'quotes', label: 'Quote', module: Enums.Module.Quote, title: 'Link Quotes', mode: 'endpoint' as const, icon: <IconFileDescription size={18} /> },
      { key: 'invoices', label: 'Invoice', module: Enums.Module.Invoice, title: 'Link Invoices', mode: 'endpoint' as const, icon: <IconFileDollar size={18} /> },
      { key: 'purchase-orders', label: 'Purchase Order', module: Enums.Module.PurchaseOrder, title: 'Link Purchase Orders', mode: 'endpoint' as const, icon: <IconReceipt size={18} /> },
  ]), []);

  return (
    <>
      <Button rightSection={<IconLinkPlus size={17} />} color={'scBlue'} variant={'outline'} onClick={() => setOptionsOpen(true)}>Add Link</Button>

      <SCModal
          size={'auto'}
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        withCloseButton
        headerSection={
          <Flex align={'center'} gap={'sm'} pt={'md'} px={'md'}>
            <IconLink size={18} />
            <Text fw={700}>Link item to job</Text>
          </Flex>
        }
      >
        <Flex direction={'column'} gap={'sm'} style={{ width: 360, maxWidth: '100%' }}>
          {options
            .filter((o: any) => !(o.key === 'projects' && !!projectId))
            .map((opt: any) => (
              <Button styles={{inner: {justifyContent: 'start'}}} key={opt.key} variant={'default'} fullWidth leftSection={opt.icon} onClick={() => {
                setOption(opt);
                setOptionsOpen(false);
                setPickerOpen(true);
                loadExcludedIds(opt.module);
              }}>
                {opt.label}
              </Button>
            ))}
        </Flex>
      </SCModal>

      {option && (
        <LinkPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onBack={() => { setPickerOpen(false); setOptionsOpen(true); }}
          onLinked={() => { onLinked(option.module, (option.mode === 'linkService' ? 'linkService' : 'endpoint') as any); setPickerOpen(false); }}
          onPick={option.mode === 'pickOnly' ? (item: any) => onPickProject(item) : undefined}
          title={option.title}
          jobId={jobId}
          customerId={customerId || null}
          module={option.module}
          queryMode={(option.mode === 'linkService' ? 'linkService' : 'endpoint') as any}
          linkType={option.linkType}
          excludedItemIdList={excludedIds}
          currencySymbol={effectiveCurrencySymbol}
        />
      )}
    </>
  );
};

export default LinkedItemsList;
